// frontend/src/lib/imagePipeline.ts
//
// Resize + re-encode + EXIF-strip user-uploaded images in the browser
// before they're PUT to R2 via presigned URLs. WebP q=0.82 everywhere.
// Spec: docs/superpowers/specs/2026-05-13-image-processing-pipeline-design.md

import type { UploadPurpose } from "../api/uploads";

// ── Constants ────────────────────────────────────────────────────────────────

export const ACCEPTED_MIMES = ["image/jpeg", "image/png", "image/webp"] as const;
export const MAX_INPUT_BYTES = 50 * 1024 * 1024; // 50 MB
export const MAX_INPUT_PIXELS_LONG_EDGE = 8000;
export const MAX_OUTPUT_BYTES = 2 * 1024 * 1024; // 2 MB
export const WEBP_QUALITY = 0.82;

const TARGET_LONG_EDGE: Record<UploadPurpose, number> = {
  profile: 800,
  "card-logo": 800,
  bg: 1600,
};

// ── Errors ───────────────────────────────────────────────────────────────────

export type ImagePipelineErrorCode =
  | "FILE_TOO_LARGE"
  | "UNSUPPORTED_HEIC"
  | "UNSUPPORTED_SVG"
  | "UNSUPPORTED_GIF"
  | "UNSUPPORTED_FORMAT"
  | "IMAGE_DECODE_FAILED"
  | "IMAGE_DIMENSIONS_TOO_LARGE"
  | "OUTPUT_TOO_LARGE"
  | "CANVAS_UNAVAILABLE";

export class ImagePipelineError extends Error {
  readonly code: ImagePipelineErrorCode;
  readonly userMessage: string;
  constructor(code: ImagePipelineErrorCode, userMessage: string) {
    super(`${code}: ${userMessage}`);
    this.name = "ImagePipelineError";
    this.code = code;
    this.userMessage = userMessage;
  }
}

// ── Validators ───────────────────────────────────────────────────────────────

export function validateInputSize(file: File): void {
  if (file.size > MAX_INPUT_BYTES) {
    throw new ImagePipelineError(
      "FILE_TOO_LARGE",
      "That file is over 50 MB. Try a smaller photo, or shrink it first.",
    );
  }
}

export function validateInputMime(file: File): void {
  const mime = file.type.toLowerCase();
  if (mime === "image/heic" || mime === "image/heif") {
    throw new ImagePipelineError(
      "UNSUPPORTED_HEIC",
      "iPhone HEIC photos aren't supported. Either share via the Photos app " +
        "(auto-converts to JPEG), or change Settings → Camera → Formats → " +
        "Most Compatible and re-take.",
    );
  }
  if (mime === "image/svg+xml") {
    throw new ImagePipelineError(
      "UNSUPPORTED_SVG",
      "SVG files aren't supported. Export your logo as PNG or JPEG from your design tool.",
    );
  }
  if (mime === "image/gif") {
    throw new ImagePipelineError(
      "UNSUPPORTED_GIF",
      "Animated GIFs aren't supported. Try a still image instead.",
    );
  }
  if (!(ACCEPTED_MIMES as readonly string[]).includes(mime)) {
    throw new ImagePipelineError(
      "UNSUPPORTED_FORMAT",
      "Unsupported file type. Please use JPEG, PNG, or WebP.",
    );
  }
}

export function validateDimensions(width: number, height: number): void {
  if (width > MAX_INPUT_PIXELS_LONG_EDGE || height > MAX_INPUT_PIXELS_LONG_EDGE) {
    throw new ImagePipelineError(
      "IMAGE_DIMENSIONS_TOO_LARGE",
      "Image is unusually large (max 8000×8000). Try a smaller source.",
    );
  }
}

export function computeTargetSize(
  srcW: number,
  srcH: number,
  purpose: UploadPurpose,
): { width: number; height: number } {
  const cap = TARGET_LONG_EDGE[purpose];
  const longEdge = Math.max(srcW, srcH);
  if (longEdge <= cap) {
    // No upscale: keep source dimensions, but the canvas pass still runs
    // (strips EXIF, normalizes format).
    return { width: srcW, height: srcH };
  }
  const scale = cap / longEdge;
  return { width: Math.round(srcW * scale), height: Math.round(srcH * scale) };
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function processImage(file: File, purpose: UploadPurpose): Promise<File> {
  validateInputSize(file);
  validateInputMime(file);
  const bitmap = await decodeOrThrow(file);
  try {
    validateDimensions(bitmap.width, bitmap.height);
    const target = computeTargetSize(bitmap.width, bitmap.height, purpose);
    const blob = await encodeWebP(bitmap, target);
    if (blob.size > MAX_OUTPUT_BYTES) {
      throw new ImagePipelineError(
        "OUTPUT_TOO_LARGE",
        "Couldn't compress that image enough — try a smaller original.",
      );
    }
    const stem = file.name.replace(/\.[^.]+$/, "") || "image";
    return new File([blob], `${stem}.webp`, { type: "image/webp" });
  } finally {
    bitmap.close();
  }
}

// ── Canvas glue (verified via Playwright in Phase 5, not unit tests) ────────

async function decodeOrThrow(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    throw new ImagePipelineError(
      "IMAGE_DECODE_FAILED",
      "That file isn't a readable image. Try a different one.",
    );
  }
}

async function encodeWebP(
  bitmap: ImageBitmap,
  target: { width: number; height: number },
): Promise<Blob> {
  const canvas = new OffscreenCanvas(target.width, target.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new ImagePipelineError(
      "CANVAS_UNAVAILABLE",
      "Your browser doesn't support 2D canvas. Try another browser.",
    );
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, target.width, target.height);
  return canvas.convertToBlob({ type: "image/webp", quality: WEBP_QUALITY });
}
